import { CopyError } from '../copy/text.js';
import { viewerStrings } from '../copy/strings.js';
import { parseRules } from '@cdr-clarity/query-service';
import type { ClassificationRuleSet, DatasetDescriptor, QueryScope } from '@cdr-clarity/contracts';
import type { WorkerClient } from '../runtime/worker-client.js';
import type { Results } from '../runtime/protocol.js';
import { parseRulesJson, readRulesFile, rulesJson, rulesStorageKey } from './rules-storage.js';

export type ClassificationRule = ClassificationRuleSet['rules'][number];
export interface RulesSnapshot {
  readonly draft: ClassificationRuleSet;
  readonly appliedRevision: string;
  readonly preview: Results['previewRules'] | null;
  readonly busy: boolean;
  readonly saved: boolean;
}

/** Edits rule definitions; interpretation, precedence and previews belong to the shared worker. */
export class RulesState {
  private snapshot: RulesSnapshot = {
    draft: { version: 1, id: 'local-rules', revision: 'none', rules: [] },
    appliedRevision: 'none',
    preview: null,
    busy: false,
    saved: false,
  };
  private readonly listeners = new Set<() => void>();
  constructor(
    private readonly client: Pick<WorkerClient, 'request'>,
    private readonly storage: Pick<Storage, 'getItem' | 'setItem'>,
    private readonly platform: string,
    private readonly newId: () => string,
    private readonly scope: () => QueryScope,
    private readonly applied: (descriptor: DatasetDescriptor) => void,
  ) {}
  getSnapshot = (): RulesSnapshot => this.snapshot;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  private publish(patch: Partial<RulesSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
  }
  private available(): void {
    if (this.snapshot.busy)
      throw new CopyError(
        viewerStrings.webStateRulesState.waitForTheClassificationOperationToFinish,
      );
  }
  private async run<T>(operation: () => Promise<T>): Promise<T> {
    this.available();
    this.publish({ busy: true });
    try {
      return await operation();
    } finally {
      this.publish({ busy: false });
    }
  }
  async restore(): Promise<void> {
    await this.run(async () => {
      const current = await this.client.request({ operation: 'exportRules' });
      this.publish({
        draft: current,
        appliedRevision: current.revision,
        preview: null,
        saved: false,
      });
      // Rebuilding a workspace must preserve worker-accepted rules, including unsaved revisions.
      // Storage is only a bootstrap source for a worker that has never accepted a ruleset.
      if (current.id !== 'none' || current.revision !== 'none' || current.rules.length !== 0)
        return;
      const saved = this.storage.getItem(rulesStorageKey(this.platform));
      if (saved === null) return;
      const rules = parseRulesJson(saved);
      const descriptor = await this.client.request({ operation: 'setRules', rules });
      this.publish({ draft: rules, appliedRevision: rules.revision, saved: true });
      this.applied(descriptor);
    });
  }
  edit(rules: readonly ClassificationRule[]): void {
    this.available();
    this.publish({
      draft: { ...this.snapshot.draft, rules: rules.map((rule, order) => ({ ...rule, order })) },
      preview: null,
      saved: false,
    });
  }
  add(): void {
    this.edit([
      ...this.snapshot.draft.rules,
      {
        id: this.newId(),
        name: '',
        order: this.snapshot.draft.rules.length,
        enabled: true,
        endpoint: 'both',
        category: 'internal',
        label: null,
        conditions: [{ field: 'number', op: 'eq', value: '' }],
      },
    ]);
  }
  validate(): ClassificationRuleSet {
    return parseRules(this.snapshot.draft);
  }
  async importFile(file: Blob): Promise<void> {
    await this.run(async () => {
      const draft = await readRulesFile(file);
      this.publish({ draft, preview: null, saved: false });
    });
  }
  exportJson(): string {
    return rulesJson(this.snapshot.draft);
  }
  async preview(size: number): Promise<void> {
    await this.run(async () => {
      const rules = parseRules({ ...this.snapshot.draft, revision: this.newId() });
      const scope = this.scope();
      const preview = await this.client.request({ operation: 'previewRules', rules, scope, size });
      if (JSON.stringify(scope) !== JSON.stringify(this.scope()))
        throw new CopyError(viewerStrings.webStateRulesState.theDatasetOrScopeChangedDuringThe);
      this.publish({ preview });
    });
  }
  async apply(): Promise<void> {
    await this.run(async () => {
      const rules = parseRules({ ...this.snapshot.draft, revision: this.newId() });
      const serialized = rulesJson(rules);
      const descriptor = await this.client.request({ operation: 'setRules', rules });
      this.publish({ draft: rules, appliedRevision: rules.revision, preview: null, saved: false });
      // Invalidate even when persistence fails: the worker has accepted this revision.
      this.applied(descriptor);
      try {
        this.storage.setItem(rulesStorageKey(this.platform), serialized);
      } catch (error) {
        throw new CopyError(viewerStrings.webStateRulesState.rulesWereAppliedButCouldNotBe, {
          cause: error,
        });
      }
      this.publish({ saved: true });
    });
  }
}
